import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchGuide, fetchGuideHistory } from '../services/publicPagesApi';
import SensorCharts, { SensorMeta, HistoryRow } from './shared/SensorCharts';
import { publicBodyStyle, publicCardStyle } from './shared/PublicPage';

interface GuideData {
    name: string;
    instruction_text: string;
    instruction_image: string | null;
    sensors: SensorMeta[];
}

const GuidePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [data, setData] = useState<GuideData | null>(null);
    const [history, setHistory] = useState<HistoryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!token) return;
        fetchGuide(token)
            .then(setData)
            .catch(err => { if (err?.response?.status === 404) setNotFound(true); })
            .finally(() => setLoading(false));
    }, [token]);

    // Once the guide is loaded, pull the last hour of readings for any temperature
    // sensors in the rule so we can render a graph per sensor.
    const hasSensors = (data?.sensors?.length ?? 0) > 0;
    useEffect(() => {
        if (!token || !hasSensors) return;
        fetchGuideHistory<HistoryRow>(token)
            .then(setHistory)
            .catch(() => { /* graph is best-effort; ignore failures */ });
    }, [token, hasSensors]);

    const bodyStyle = publicBodyStyle;
    const cardStyle = publicCardStyle(720);

    if (loading) {
        return (
            <div style={bodyStyle}>
                <div style={cardStyle}>
                    <p style={{ color: '#888' }}>Hämtar…</p>
                </div>
            </div>
        );
    }

    if (notFound || !data) {
        return (
            <div style={bodyStyle}>
                <div style={cardStyle}>
                    <h1 style={{ color: '#c0392b' }}>Hittade ingen beskrivning</h1>
                    <p style={{ color: '#666' }}>Länken kan vara fel, eller så har larmregeln tagits bort.</p>
                </div>
            </div>
        );
    }

    return (
        <div style={bodyStyle}>
            <div style={cardStyle}>
                <h1 style={{ margin: '0 0 8px', fontSize: '22px', color: '#c0392b' }}>
                    ⚠️ Larm: {data.name}
                </h1>
                <h2 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 400, color: '#555' }}>
                    Så här gör du
                </h2>
                <div style={{ fontSize: '17px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {data.instruction_text || <em style={{ color: '#999' }}>Ingen beskrivning är ifylld.</em>}
                </div>
                {data.instruction_image && (
                    <div style={{ marginTop: '24px', textAlign: 'center' }}>
                        <img
                            src={data.instruction_image}
                            alt="Bild till beskrivningen"
                            style={{ maxWidth: '100%', maxHeight: '600px', borderRadius: '8px', border: '1px solid #ddd' }}
                        />
                    </div>
                )}
                <div style={{ marginTop: 24 }}>
                    <SensorCharts sensors={data.sensors} history={history} />
                </div>
                <div style={{ textAlign: 'center', marginTop: '32px', fontSize: '12px', color: '#aaa' }}>
                    DevIoT – beskrivning vid larm
                </div>
            </div>
        </div>
    );
};

export default GuidePage;
